import dagre from '@dagrejs/dagre';

export type LayoutDirection = 'TB' | 'LR';

export interface InferLayoutNode {
  id: string;
  position: { x: number; y: number };
}

export interface InferLayoutEdge {
  source: string;
  target: string;
}

/**
 * Guess whether a saved graph was laid out vertically or horizontally from
 * the relative positions of connected nodes. Used to seed the editor's
 * layout-direction toggle when the direction is not persisted.
 */
export function inferLayoutDirection(
  nodes: ReadonlyArray<InferLayoutNode>,
  edges: ReadonlyArray<InferLayoutEdge>,
): LayoutDirection {
  if (nodes.length < 2 || edges.length === 0) return 'LR';

  const posById = new Map(nodes.map((n) => [n.id, n.position]));
  let sumDx = 0;
  let sumDy = 0;

  for (const edge of edges) {
    const src = posById.get(edge.source);
    const tgt = posById.get(edge.target);
    if (!src || !tgt || edge.source === edge.target) continue;
    sumDx += Math.abs(tgt.x - src.x);
    sumDy += Math.abs(tgt.y - src.y);
  }

  if (sumDx === 0 && sumDy === 0) return 'LR';
  return sumDy > sumDx ? 'TB' : 'LR';
}

export interface LayoutNode {
  id: string;
  type?: string;
}

export interface LayoutEdge {
  source: string;
  target: string;
}

export interface NodeSize {
  width: number;
  height: number;
}

export interface LayoutInput {
  nodes: ReadonlyArray<LayoutNode>;
  edges: ReadonlyArray<LayoutEdge>;
  direction?: LayoutDirection;
  /**
   * Measured per-node sizes keyed by node id. Used when available so the
   * layout reflects the real on-screen footprint. Falls back to
   * `defaultNodeSize` otherwise.
   */
  nodeSizes?: ReadonlyMap<string, NodeSize>;
  /**
   * Per-node-type fallback sizes. Used when the node has no measured size
   * in `nodeSizes`. Anything not in this map falls back to `defaultNodeSize`.
   */
  defaultNodeSizesByType?: Readonly<Record<string, NodeSize>>;
  defaultNodeSize?: NodeSize;
  /** Spacing between adjacent ranks (perpendicular to the flow direction). */
  rankSep?: number;
  /** Spacing between sibling nodes within a rank. */
  nodeSep?: number;
  /** Spacing between adjacent edges. */
  edgeSep?: number;
}

export type LayoutResult = Map<string, { x: number; y: number }>;

export interface PositionedNode {
  id: string;
  position: { x: number; y: number };
}

export interface PositionedEdge {
  source: string;
  target: string;
}

/**
 * Find the entry nodes of a flow graph — the nodes a synthetic trigger node
 * should connect to.
 *
 * Entry nodes are determined topologically: any node with no incoming edge.
 * For fully cyclic graphs (every node has an incoming edge), edges drawn
 * visually backwards (right-to-left) are treated as loop-backs and ignored;
 * if that still yields nothing, the leftmost node(s) win.
 */
export function findEntryNodes<N extends PositionedNode>(
  nodes: ReadonlyArray<N>,
  edges: ReadonlyArray<PositionedEdge>,
): N[] {
  if (nodes.length === 0) return [];
  const nodeIds = new Set(nodes.map((n) => n.id));
  const hasIncoming = new Set<string>();
  for (const e of edges) {
    if (nodeIds.has(e.source) && nodeIds.has(e.target) && e.source !== e.target) {
      hasIncoming.add(e.target);
    }
  }
  const entries = nodes.filter((n) => !hasIncoming.has(n.id));
  if (entries.length > 0) return entries;

  const posById = new Map(nodes.map((n) => [n.id, n.position]));
  const forwardTargets = new Set<string>();
  for (const e of edges) {
    const srcPos = posById.get(e.source);
    const tgtPos = posById.get(e.target);
    if (srcPos && tgtPos && srcPos.x < tgtPos.x) {
      forwardTargets.add(e.target);
    }
  }
  const forwardEntries = nodes.filter((n) => !forwardTargets.has(n.id));
  if (forwardEntries.length > 0) return forwardEntries;

  const minX = Math.min(...nodes.map((n) => n.position.x));
  return nodes.filter((n) => n.position.x === minX);
}

/** Edge subset needed to resolve which source handle an edge leaves from. */
export interface HandleEdge {
  source: string;
  target: string;
  sourceHandle?: string | null;
}

/**
 * Rank separation that leaves room for the widest edge label to render in
 * the gap between ranks instead of being buried under the nodes. Labels are
 * drawn centered on the edge path at ~11px, so the gap must be at least the
 * label's width plus breathing room on both sides.
 */
export function rankSepForEdgeLabels(
  labels: ReadonlyArray<string | null | undefined>,
  { min = 80, max = 280, padding = 48 }: { min?: number; max?: number; padding?: number } = {},
): number {
  let widest = 0;
  for (const label of labels) {
    // ~6.5px per character at 11px font weight 500, plus 12px label padding.
    if (label) widest = Math.max(widest, Math.round(label.length * 6.5) + 12);
  }
  if (widest === 0) return min;
  return Math.max(min, Math.min(max, widest + padding));
}

/**
 * Order an approval node's decision options so its per-option source handles
 * stack in the same vertical order as the targets they connect to. Without
 * this, the handles follow authoring order while the layout places targets
 * in crossing-minimized order, and disagreement makes conditional edges
 * cross each other immediately at the source.
 *
 * Options without a connected edge sink to the end, keeping their relative
 * order; connected options tie-break by their original order too, so the
 * sort is fully stable.
 */
export function orderDecisionOptionsByTarget<O extends { value: string }>(
  options: ReadonlyArray<O>,
  nodeId: string,
  edges: ReadonlyArray<HandleEdge>,
  targetYById: ReadonlyMap<string, number>,
): O[] {
  const yFor = (value: string): number => {
    const edge = edges.find((e) => e.source === nodeId && e.sourceHandle === value);
    const y = edge ? targetYById.get(edge.target) : undefined;
    return y ?? Number.POSITIVE_INFINITY;
  };
  return options
    .map((option, index) => ({ option, index, y: yFor(option.value) }))
    .sort((a, b) => a.y - b.y || a.index - b.index)
    .map((entry) => entry.option);
}

const DEFAULT_NODE_SIZE: NodeSize = { width: 220, height: 80 };

const DEFAULT_TYPE_SIZES: Readonly<Record<string, NodeSize>> = {
  trigger: { width: 240, height: 100 },
  approval: { width: 240, height: 90 },
  router: { width: 220, height: 90 },
  agent: { width: 220, height: 90 },
  agent_task: { width: 220, height: 100 },
  llm: { width: 220, height: 90 },
  action: { width: 220, height: 80 },
};

/**
 * Compute a layered left-right (or top-down) layout for a flow graph using dagre.
 *
 * Defaults to 'LR' because flow nodes render their target handle on the left
 * and source handle on the right; a 'TB' layout forces every edge to wrap
 * back around its nodes.
 *
 * Pure function — no React, no DOM. Returns top-left coordinates suitable for
 * React Flow's `node.position` field.
 */
export function layoutGraph({
  nodes,
  edges,
  direction = 'LR',
  nodeSizes,
  defaultNodeSizesByType,
  defaultNodeSize = DEFAULT_NODE_SIZE,
  rankSep = 80,
  nodeSep = 60,
  edgeSep = 20,
}: LayoutInput): LayoutResult {
  const result: LayoutResult = new Map();
  if (nodes.length === 0) return result;

  const g = new dagre.graphlib.Graph({ multigraph: true, compound: false });
  g.setGraph({ rankdir: direction, ranksep: rankSep, nodesep: nodeSep, edgesep: edgeSep });
  g.setDefaultEdgeLabel(() => ({}));

  const sizeFor = (node: LayoutNode): NodeSize => {
    const measured = nodeSizes?.get(node.id);
    if (measured && measured.width > 0 && measured.height > 0) return measured;
    if (node.type) {
      const typed = defaultNodeSizesByType?.[node.type] ?? DEFAULT_TYPE_SIZES[node.type];
      if (typed) return typed;
    }
    return defaultNodeSize;
  };

  const sizeById = new Map<string, NodeSize>();
  for (const node of nodes) {
    const size = sizeFor(node);
    sizeById.set(node.id, size);
    g.setNode(node.id, { width: size.width, height: size.height });
  }

  const nodeIdSet = new Set(nodes.map((n) => n.id));
  let edgeCounter = 0;
  for (const edge of edges) {
    if (!nodeIdSet.has(edge.source) || !nodeIdSet.has(edge.target)) continue;
    g.setEdge(edge.source, edge.target, {}, `e${edgeCounter++}`);
  }

  dagre.layout(g);

  for (const node of nodes) {
    const laidOut = g.node(node.id);
    if (!laidOut) continue;
    const size = sizeById.get(node.id) ?? defaultNodeSize;
    // dagre returns the center of each node; React Flow uses top-left coords.
    result.set(node.id, {
      x: laidOut.x - size.width / 2,
      y: laidOut.y - size.height / 2,
    });
  }

  return result;
}
